import {
  describeValue,
  isMarkerKind,
  wordFor,
  type CheckInTemplate,
  type Entry,
  type Tag,
  type Track,
  type TrackValue,
  type ValidationError,
} from '@anytracker/core'
import { useCallback, useEffect, useState, type CSSProperties } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { checkInColour, paletteVar, trackFill, trackTint } from '../../charts/colours.js'
import { Button } from '../../components/Button.js'
import { Chip } from '../../components/Chip.js'
import { EmptyState } from '../../components/EmptyState.js'
import {
  ActionBar,
  ActionButton,
  ICONS,
  Icon,
  Page,
  Section,
  SubHeader,
  Swatch,
} from '../../components/Layout.js'
import { useToast } from '../../components/Toast.js'
import { ValueInput } from '../../components/value-input/ValueInput.js'
import { formatDayHeading, formatTime } from '../../format/dates.js'
import { TagPicker } from '../log/TagPicker.js'
import { UnsavedWorkGuard, useDiscardGuard } from '../../shell/DiscardGuard.js'
import { useServices } from '../../services/ServicesProvider.js'
import formStyles from '../log/EntryForm.module.css'
import styles from './Timeline.module.css'

/**
 * One entry: read first, edited on request (T-306, Meadow redesign).
 *
 * Corrections are routine: a value entered in haste, a missing note, a
 * timestamp that was actually forty minutes earlier. Without editing, people
 * either abandon flawed entries or stop trusting the record — and a record
 * nobody trusts is not evidence.
 *
 * It opens as a reading of what was recorded, because that is what most visits
 * are for, and "Edit" turns the same screen into the form. Every edit the form
 * offers is also reachable from the reading: "Add" beside a track the check-in
 * asks about but this entry lacks, and "Add tag", both open the form at that
 * point rather than being a second, smaller editor.
 */
export function EntryDetailScreen() {
  const { repos, clock } = useServices()
  const { show } = useToast()
  const navigate = useNavigate()
  const entryId = useParams()['id'] ?? ''

  const [entry, setEntry] = useState<Entry | null>(null)
  const [template, setTemplate] = useState<CheckInTemplate | null>(null)
  const [tracks, setTracks] = useState<Track[]>([])
  const [tags, setTags] = useState<Tag[]>([])
  const [values, setValues] = useState<Record<string, TrackValue>>({})
  const [tagIds, setTagIds] = useState<string[]>([])
  const [note, setNote] = useState('')
  const [at, setAt] = useState<Entry['at'] | null>(null)
  const [errors, setErrors] = useState<readonly ValidationError[]>([])
  const [dirty, setDirty] = useState(false)
  const [editing, setEditing] = useState(false)
  const [notFound, setNotFound] = useState(false)

  // One flag for the update-prompt deferral, the indicator and the discard
  // confirmation (T-204, T-808).
  const guard = useDiscardGuard(dirty)

  /** The form's state, from the stored entry. Also how Cancel discards. */
  const adopt = useCallback((found: Entry) => {
    setValues(Object.fromEntries(found.measurements.map((m) => [m.trackId, m.value])))
    setTagIds([...found.tagIds])
    setNote(found.note ?? '')
    setAt(found.at)
  }, [])

  const load = useCallback(async () => {
    const [found, allTracks, allTags] = await Promise.all([
      repos.entries.get(entryId),
      repos.tracks.list({ includeArchived: true }),
      repos.tags.list(),
    ])
    setTracks(allTracks)
    setTags(allTags)
    if (!found) {
      setNotFound(true)
      return
    }
    setTemplate(
      found.templateId === undefined
        ? null
        : ((await repos.templates.get(found.templateId)) ?? null),
    )
    setEntry(found)
    adopt(found)
  }, [entryId, repos, adopt])

  useEffect(() => {
    void load()
  }, [load])

  /*
   * Writes, and reports whether it worked — without navigating. See the same
   * split in the track editor.
   */
  async function persist(): Promise<boolean> {
    if (!entry || !at) {
      return false
    }
    setErrors([])
    const measurements = Object.entries(values).map(([trackId, value]) => {
      const kept = entry.measurements.find((m) => m.trackId === trackId)?.note
      return { trackId, value, ...(kept === undefined ? {} : { note: kept }) }
    })

    const result = await repos.entries.update(entry.id, {
      at,
      tagIds,
      measurements,
      ...(note.trim() === '' ? { note: undefined } : { note: note.trim() }),
    })

    if (!result.ok) {
      setErrors(result.errors)
      return false
    }
    setDirty(false)
    guard.allowLeaving()
    show({ message: 'Saved.' })
    return true
  }

  async function save() {
    if (await persist()) {
      void navigate('/timeline')
    }
  }

  /** Back to the reading, with the stored entry as it was. */
  function cancel() {
    if (entry) {
      adopt(entry)
    }
    setErrors([])
    setDirty(false)
    setEditing(false)
  }

  /*
   * Seeded from the track's own minimum where it has one, so the control opens
   * somewhere valid rather than empty. A marker is only ever `true`, which is
   * also the only thing worth seeding it with.
   */
  function addMeasurement(track: Track) {
    const seed: TrackValue =
      track.kind.kind === 'numeric'
        ? track.kind.min
        : track.kind.kind === 'boolean' || track.kind.kind === 'marker'
          ? true
          : (track.kind.options[0]?.id ?? '')
    setValues((current) => ({ ...current, [track.id]: seed }))
    setDirty(true)
    setEditing(true)
  }

  async function createTag(name: string) {
    const result = await repos.tags.create(name)
    if (!result.ok) {
      show({
        message: result.errors[0]?.message ?? 'Could not add that.',
        tone: 'danger',
      })
      return
    }
    setTags(await repos.tags.list())
    setTagIds((current) => [...current, result.value.id])
    setDirty(true)
  }

  async function remove() {
    if (!entry) {
      return
    }
    const result = await repos.entries.remove(entry.id)
    if (!result.ok) {
      show({ message: 'Could not delete that.', tone: 'danger' })
      return
    }
    const removed = result.value
    void navigate('/timeline')
    /*
     * Undo, not a confirmation dialog.
     *
     * Confirmations get dismissed reflexively — the tap is muscle memory
     * before the sentence is read. Undo catches the mistake once it has been
     * *noticed*, which is when people actually notice. `restore` puts the entry
     * back with its original id, so anything holding a reference still points
     * at something that exists (T-107).
     *
     * Not the danger tone: that means "this failed" everywhere else, and this
     * succeeded. The undo button is the emphasis a destructive action needs.
     */
    show({
      message: 'Entry deleted.',
      action: {
        label: 'Undo',
        onAction: () => {
          void repos.entries.restore(removed).then(() => {
            show({ message: 'Entry restored.' })
          })
        },
      },
    })
  }

  if (notFound) {
    return (
      /*
       * Not defensive trivia. Two tabs is enough to produce this, and so is a
       * bookmark from before a deletion — and the alternative to a screen is a
       * blank page with no way out of it.
       */
      <Page>
        <SubHeader back={{ to: '/timeline', label: 'Timeline' }} />
        <h1>Entry</h1>
        <EmptyState
          title="That entry is not here any more"
          body="It was probably deleted — in another tab, or before this link was opened. Nothing else has changed."
          action={
            <Button variant="primary" onClick={() => void navigate('/timeline')}>
              Back to the timeline
            </Button>
          }
        />
      </Page>
    )
  }

  if (!entry || !at) {
    return (
      <Page>
        <SubHeader back={{ to: '/timeline', label: 'Timeline' }} />
        <h1>Entry</h1>
        <p>Loading…</p>
      </Page>
    )
  }

  const byId = new Map(tracks.map((track) => [track.id, track]))
  const measured = tracks.filter((track) => values[track.id] !== undefined)
  const addable = tracks.filter(
    (track) => !track.archived && values[track.id] === undefined,
  )
  /*
   * Tracks the check-in asks about that this entry has no value for.
   *
   * Shown as absent, never as zero: skipping a question records nothing, and
   * the reading has to say "not recorded" rather than imply a value.
   */
  const missing = (template?.trackIds ?? []).flatMap((id) => {
    const track = byId.get(id)
    return track !== undefined && !track.archived && values[id] === undefined
      ? [track]
      : []
  })
  /*
   * Measurements whose track no longer exists at all.
   *
   * Permanent deletion is only offered for tracks with no history, so this
   * should be unreachable — but "should be unreachable" is not a rendering
   * strategy, and a backup restored from a future version is enough to produce
   * one. Filtering them out silently would make a measurement invisible on the
   * one screen that exists to show what an entry holds.
   */
  const orphans = Object.entries(values).filter(([trackId]) => !byId.has(trackId))
  const chosenTags = tagIds.flatMap((id) => {
    const tag = tags.find((candidate) => candidate.id === id)
    return tag === undefined ? [] : [tag]
  })

  const colour = template === null ? null : checkInColour(template)
  const heroStyle =
    colour === null
      ? undefined
      : ({
          '--hero-tint': paletteVar(colour, 'tint'),
          '--hero-edge': paletteVar(colour, 'edge'),
          '--hero-deep': paletteVar(colour, 'deep'),
          '--hero-fill': paletteVar(colour, 'fill'),
        } as CSSProperties)

  return (
    <Page>
      <UnsavedWorkGuard guard={guard} what="This entry" onSave={persist} />

      {/*
        The check-in's colour behind the head of the screen, so an entry is
        recognisable before a word of it is read. An entry logged outside any
        check-in gets the neutral wash.
      */}
      <header className={styles['hero']} style={heroStyle} data-bleed>
        <div className={styles['heroNav']}>
          <Link to="/timeline" className={styles['heroLink']}>
            <Icon path={ICONS.chevronLeft} />
            Timeline
          </Link>
          {!editing && (
            <button
              type="button"
              className={styles['heroLink']}
              onClick={() => {
                setEditing(true)
              }}
            >
              Edit
            </button>
          )}
        </div>
        <p className={styles['heroEyebrow']}>
          <span className={styles['heroMark']} aria-hidden="true" />
          {template === null ? 'Entry' : 'Check-in'}
        </p>
        <h1>{template?.name ?? 'Entry'}</h1>
        <p className={styles['heroWhen']}>
          {formatDayHeading(at, clock.now())},{' '}
          <span data-testid="entry-when">{formatTime(at)}</span>
          {' · '}
          {/*
            Always, not only when it differs from the device's. This is the
            detail screen and the stored zone is the point: it is what makes
            the time above mean anything after a flight (ADR-0006).
          */}
          <span data-testid="entry-zone">{zoneName(at.timeZone)}</span>
        </p>
      </header>

      {editing ? (
        <>
          <Section label="When" id="when" padded>
            <input
              type="datetime-local"
              className={formStyles['datetime']}
              aria-label="Date and time"
              value={toLocalInput(at)}
              onChange={(event) => {
                const parsed = Date.parse(event.target.value)
                if (Number.isFinite(parsed)) {
                  // Zone preserved: correcting the time does not relocate the
                  // entry (ADR-0006).
                  setAt({ epochMs: parsed, timeZone: at.timeZone })
                  setDirty(true)
                }
              }}
            />
          </Section>

          {(measured.length > 0 || orphans.length > 0) && (
            <Section label="Measurements" id="edit-measurements">
              {measured.map((track) => (
                <div key={track.id} className={formStyles['slot']}>
                  <div className={formStyles['slotHeader']}>
                    <span className={formStyles['slotName']} id={`label-${track.id}`}>
                      <Swatch colour={trackFill(track)} size={8} />
                      {track.name}
                      {track.archived ? ' (archived)' : ''}
                    </span>
                    {track.unit !== undefined && (
                      <span className={formStyles['slotUnit']}>{track.unit}</span>
                    )}
                  </div>
                  <ValueInput
                    track={track}
                    value={values[track.id] ?? null}
                    labelledBy={`label-${track.id}`}
                    onChange={(next) => {
                      setValues((current) => {
                        const updated = { ...current }
                        if (next === null) {
                          delete updated[track.id]
                        } else {
                          updated[track.id] = next
                        }
                        return updated
                      })
                      setDirty(true)
                    }}
                  />
                  <button
                    type="button"
                    className={formStyles['skip']}
                    onClick={() => {
                      setValues((current) => {
                        const updated = { ...current }
                        delete updated[track.id]
                        return updated
                      })
                      setDirty(true)
                    }}
                  >
                    Remove {track.name} from this entry
                  </button>
                </div>
              ))}

              {orphans.map(([trackId, value]) => (
                <div key={trackId} className={formStyles['slot']}>
                  <Orphan trackId={trackId} value={value} />
                  <button
                    type="button"
                    className={formStyles['skip']}
                    onClick={() => {
                      setValues((current) => {
                        const updated = { ...current }
                        delete updated[trackId]
                        return updated
                      })
                      setDirty(true)
                    }}
                  >
                    Remove it from this entry
                  </button>
                </div>
              ))}
            </Section>
          )}

          {addable.length > 0 && (
            <Section label="Add another measurement" id="add" padded>
              <div className={formStyles['tags']}>
                {addable.map((track) => (
                  <Chip
                    key={track.id}
                    onClick={() => {
                      addMeasurement(track)
                    }}
                  >
                    + {track.name}
                  </Chip>
                ))}
              </div>
            </Section>
          )}

          <Section label="Tags" id="edit-tags" padded>
            {/*
              The same picker the log flow uses, which carries "+ New tag", so
              an entry that turns out to need a tag can get one here.
            */}
            <TagPicker
              tags={tags}
              selected={tagIds}
              onToggle={(tagId) => {
                setTagIds(
                  tagIds.includes(tagId)
                    ? tagIds.filter((id) => id !== tagId)
                    : [...tagIds, tagId],
                )
                setDirty(true)
              }}
              onCreate={createTag}
            />
          </Section>

          <Section label="Note" id="edit-note" padded>
            <textarea
              className={formStyles['note']}
              aria-label="Note about this entry"
              value={note}
              onChange={(event) => {
                setNote(event.target.value)
                setDirty(true)
              }}
            />
          </Section>

          {errors.length > 0 && (
            <div className={formStyles['errors']} role="alert">
              {errors.map((error) => (
                <span key={error.code} className={formStyles['error']}>
                  {error.message}
                </span>
              ))}
            </div>
          )}

          <ActionBar>
            <ActionButton onClick={cancel}>Cancel</ActionButton>
            <ActionButton primary onClick={() => void save()}>
              Save changes
            </ActionButton>
          </ActionBar>
        </>
      ) : (
        <>
          <Section label="Measurements" id="measurements">
            {measured.map((track) => (
              <Measurement
                key={track.id}
                track={track}
                value={values[track.id] ?? null}
              />
            ))}
            {orphans.map(([trackId, value]) => (
              <div key={trackId} className={styles['measure']}>
                <Orphan trackId={trackId} value={value} />
              </div>
            ))}
            {missing.map((track) => (
              <div key={track.id} className={styles['missing']}>
                <span className={styles['missingName']}>
                  <Swatch colour={trackFill(track)} size={8} outline />
                  {track.name} · not recorded
                </span>
                <button
                  type="button"
                  className={styles['inlineAction']}
                  aria-label={`Add ${track.name}`}
                  onClick={() => {
                    addMeasurement(track)
                  }}
                >
                  Add
                </button>
              </div>
            ))}
          </Section>

          <Section label="Tags" id="tags" padded>
            <div className={styles['tagList']}>
              {chosenTags.map((tag) => (
                <Chip
                  key={tag.id}
                  {...(tag.colour === undefined ? {} : { colour: tag.colour })}
                >
                  {tag.name}
                </Chip>
              ))}
              <button
                type="button"
                className={styles['addTag']}
                onClick={() => {
                  setEditing(true)
                }}
              >
                Add tag
              </button>
            </div>
          </Section>

          {entry.note !== undefined && (
            <Section label="Note" id="note" padded>
              <p className={styles['noteText']}>{entry.note}</p>
            </Section>
          )}

          <button
            type="button"
            className={styles['deleteEntry']}
            onClick={() => void remove()}
          >
            Delete entry
          </button>
        </>
      )}
    </Page>
  )
}

/**
 * One recorded value, read.
 *
 * A number gets its place on the scale as a row of cells in the track's colour,
 * so "7 of 10" is visible before it is read. Everything else is its label,
 * resolved through the track's own configuration.
 */
function Measurement({
  track,
  value,
}: {
  readonly track: Track
  readonly value: TrackValue | null
}) {
  const kind = track.kind
  const numeric = kind.kind === 'numeric' && typeof value === 'number' ? kind : null
  const word =
    numeric !== null && typeof value === 'number' ? wordFor(numeric, value) : null
  const shown = value === null ? '' : (describeValue(track, value) ?? String(value))

  // Bar geometry only — where the value sits between the track's own bounds.
  const cells =
    numeric === null
      ? 0
      : Math.max(1, Math.min(20, Math.round((numeric.max - numeric.min) / numeric.step)))
  const filled =
    numeric === null || typeof value !== 'number' || numeric.max === numeric.min
      ? 0
      : Math.round(((value - numeric.min) / (numeric.max - numeric.min)) * cells)

  return (
    <div className={styles['measure']}>
      <div className={styles['measureHead']}>
        <span className={styles['measureName']}>
          <Swatch colour={trackFill(track)} size={8} />
          {track.name}
          {track.archived ? ' (archived)' : ''}
        </span>
        <span className={styles['measureValue']}>
          {isMarkerKind(kind) ? (
            'Recorded'
          ) : numeric !== null ? (
            <>
              <strong>{shown}</strong>
              {track.unit === undefined ? '' : ` ${track.unit}`} / {String(numeric.max)}
              {word === null ? '' : ` · ${word}`}
            </>
          ) : (
            <strong>
              {shown}
              {track.unit === undefined ? '' : ` ${track.unit}`}
            </strong>
          )}
        </span>
      </div>
      {numeric !== null && (
        <div
          className={styles['bar']}
          style={
            {
              gridTemplateColumns: `repeat(${String(cells)}, minmax(0, 1fr))`,
              // The fill, not the deep shade: the bar repeats the number written
              // beside it, so it is decoration and can be the brighter colour.
              '--bar-on': trackFill(track),
              '--bar-off': trackTint(track),
            } as CSSProperties
          }
          aria-hidden="true"
        >
          {Array.from({ length: cells }, (_, index) => (
            <span
              key={index}
              className={index < filled ? styles['barOn'] : styles['barOff']}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/** A value whose track has gone entirely: readable rather than absent. */
function Orphan({
  trackId,
  value,
}: {
  readonly trackId: string
  readonly value: TrackValue
}) {
  return (
    <>
      <span className={formStyles['slotName']}>Track no longer exists</span>
      <p className={styles['orphanValue']} data-testid="orphan-measurement">
        {String(value)}
        <span className={styles['orphanId']}>{trackId}</span>
      </p>
    </>
  )
}

/**
 * A zone as a person says it: "Europe/London" is "London time".
 *
 * The stored IANA name is the record; this is how it reads at a glance. The
 * last segment is the city, which is what people name a zone by.
 */
function zoneName(zone: string): string {
  const city = zone.split('/').pop() ?? zone
  return `${city.replace(/_/g, ' ')} time`
}

/** `<input type="datetime-local">` wants wall-clock text with no zone. */
function toLocalInput(at: Entry['at']): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: at.timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(at.epochMs)
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '00'
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`
}
